import { Box, BoxProps } from "@chakra-ui/react"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useLocation } from "react-router-dom"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { highlightCoverPageSection } from "../coverPageHashNavigation"
import { CoverPagePanelSectionKey } from "../types"
import { coverPageRevealOnHoverCss } from "../ui"
import { requestCoverPagePanelFieldFocus } from "./panelFieldFocus"
import { getCoverPagePanelSectionLinkTo } from "./utils"

/** Put this attribute on the element whose hover or focus reveals its cue. */
export const COVER_PAGE_INLINE_EDIT_ATTR = "data-cover-inline-edit"

/** Spread onto the element a {@link CoverPageInlineEditCue} belongs to. */
export const coverPageInlineEditTarget = () => ({
  [COVER_PAGE_INLINE_EDIT_ATTR]: ""
})

type CoverPageInlineEditCueProps = BoxProps & {
  /** Names the action, e.g. "Edit title". */
  label: string
  /** The panel section that edits this element. */
  sectionKey: CoverPagePanelSectionKey
  /** A field in that panel's form to focus on arrival. */
  field?: string
}

/**
 * A bare pencil for editing an inline element that sits on a background we do
 * not control: a title or line of text over a photo, or an image itself. It
 * has no pill behind it, only a soft shadow to stay legible on any image, so
 * it reads as part of the element. It appears on hover or focus of the
 * element marked with {@link coverPageInlineEditTarget}.
 *
 * It follows the text: place it right after inline content, or position it
 * with Box props over an image. Size it with `fontSize`.
 */
export function CoverPageInlineEditCue({
  label,
  sectionKey,
  field,
  ...boxProps
}: CoverPageInlineEditCueProps) {
  const { pathname } = useLocation()
  const isEditable = useCoverPageIsEditable()
  const immersiveActive = useCoverPageContext((value) => value.immersiveActive)

  if (!isEditable || immersiveActive) return null

  return (
    <Box
      as="span"
      display="inline-flex"
      verticalAlign="middle"
      ml="0.4em"
      // Never inherit a heading's indent, shadow, or underline.
      textIndent={0}
      textShadow="none"
      lineHeight={1}
      css={coverPageRevealOnHoverCss(
        `[${COVER_PAGE_INLINE_EDIT_ATTR}]:hover &, [${COVER_PAGE_INLINE_EDIT_ATTR}]:focus-within &`
      )}
      {...boxProps}
    >
      <Link
        to={getCoverPagePanelSectionLinkTo(pathname, sectionKey)}
        aria-label={label}
        title={label}
        display="inline-flex"
        p="0.25em"
        m="-0.25em"
        borderRadius="full"
        color="white"
        opacity={0.8}
        // Legible over light and dark imagery alike.
        filter="drop-shadow(0 1px 2px rgba(0, 0, 0, 0.55))"
        textDecoration="none"
        transition="opacity 0.2s, transform 0.2s"
        _hover={{ opacity: 1, transform: "scale(1.1)", textDecoration: "none" }}
        _focusVisible={{ opacity: 1 }}
        onClick={() => {
          highlightCoverPageSection(sectionKey, { highlightMain: false })
          if (field) requestCoverPagePanelFieldFocus(field)
        }}
      >
        <Pathicon icon="edit" size="1em" />
      </Link>
    </Box>
  )
}
